import { createContext, useContext, type ReactNode } from "react";

/** Whether the markdown being rendered belongs to a turn that is still
 *  streaming.
 *
 *  `MarkdownView` already takes this as a prop, but its component overrides live
 *  in a module-scope const — they can't call hooks and can't close over render
 *  state, so the prop cannot reach them directly. Same constraint, same answer,
 *  as `UserQuestionContext`: publish the scalar and let the overrides' children
 *  read it.
 *
 *  The one consumer that needs it is the table export menu. Mid-stream a table
 *  arrives row by row, so an export taken then is a partial table that looks
 *  complete — the control is hidden rather than disabled, which would invite a
 *  click and explain nothing. */
const StreamingContext = createContext(false);

export function StreamingProvider({
  streaming,
  children,
}: {
  streaming: boolean;
  children: ReactNode;
}) {
  // The value IS the boolean, so consumers re-render only when it flips (once,
  // when the turn settles) rather than on every streamed token.
  return (
    <StreamingContext.Provider value={streaming}>
      {children}
    </StreamingContext.Provider>
  );
}

/** False outside a provider: a standalone render (an artifact preview, a
 *  subagent result, a test) is settled text by definition. */
export function useIsStreaming(): boolean {
  return useContext(StreamingContext);
}
